import { get, post, patch, put, del } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR, { mutate } from 'swr';

// SWR hooks for the LivisiblyEntityApi (account-ownership Phase 1). Bodies match
// the backend contract exactly (flat — not wrapped in an *_data key).

const API_NAME = 'LivisiblyEntityApi';
const ENTITY_NETWORK_KEY = 'ENTITY_NETWORK';
const DUP_SUGGESTIONS_KEY = 'ENTITY_DUP_SUGGESTIONS';

async function getEntityNetwork() {
  const restOperation = get({ apiName: API_NAME, path: 'entities/network' });
  const response = await restOperation.response;
  return processResponse(response);
}

// The network ({entities, edges}) is the single source the editor renders from.
export function useGetEntityNetwork(shouldFetch = true, options = {}) {
  return useSWR(
    shouldFetch ? ENTITY_NETWORK_KEY : null,
    () => getEntityNetwork(),
    { revalidateOnFocus: false, revalidateOnReconnect: false, ...options },
  );
}

export const createEntity = async (entityData) => {
  const restOperation = post({ apiName: API_NAME, path: 'entities', options: { body: entityData } });
  const response = await restOperation.response;
  return processResponse(response);
};

export function useCreateEntity() {
  // POST, then insert the new entity into the cached network straight from the
  // response (real server id — no temp-id reconciliation) so the list / graph /
  // picker show it without waiting for a refetch; revalidate in the background to
  // true up. Returns the created entity — callers (e.g. the account Title dialog's
  // inline-create) read created.id.
  return async (entityData) => {
    const created = await createEntity(entityData);
    mutate(
      ENTITY_NETWORK_KEY,
      (current) => (current
        ? { ...current, entities: [...(current.entities || []), { ...entityData, ...created }] }
        : current),
      { revalidate: true, populateCache: true },
    );
    return created;
  };
}

export const updateEntity = async (entityId, updates) => {
  const restOperation = patch({ apiName: API_NAME, path: `entities/${entityId}`, options: { body: updates } });
  const response = await restOperation.response;
  return processResponse(response);
};

// Merge a field update into the cached entity — optimistic patch for
// useUpdateEntity so the list/graph react instantly; the network refetch then
// trues up. Extra keys in `updates` (e.g. `name`) are harmless on the node.
// The PATCH body carries `dba_names` (write shape) but the UI reads the serialized
// `dbas`, so map it — otherwise trade-name edits don't appear until the (slow)
// revalidation lands.
export const _applyEntityUpdate = (network, entityId, updates) => {
  if (!network) return network;
  const patch = updates.dba_names !== undefined ? { ...updates, dbas: updates.dba_names } : updates;
  return { ...network, entities: (network.entities || []).map((e) => (e.id === entityId ? { ...e, ...patch } : e)) };
};

export function useUpdateEntity() {
  return (entityId, updates) => mutate(
    ENTITY_NETWORK_KEY,
    async (current) => { await updateEntity(entityId, updates); return _applyEntityUpdate(current, entityId, updates); },
    {
      optimisticData: (current) => _applyEntityUpdate(current, entityId, updates),
      rollbackOnError: true,
      revalidate: true,
    },
  );
}

// Show/hide an entity on the ownership chart — view-only (hide_from_chart).
// OPTIMISTIC: flip the flag in the cached network immediately so the list/graph
// react with no round-trip wait, then persist via the generic entity update and
// roll back if it fails. (The plain useUpdateEntity waits for PATCH + a full
// network refetch, which is the lag.)
const _applyChartVisibility = (network, entityId, hidden) => (
  network
    ? { ...network, entities: (network.entities || []).map((e) => (e.id === entityId ? { ...e, hide_from_chart: hidden } : e)) }
    : network
);

export function useSetEntityChartVisibility() {
  return (entityId, hidden) => mutate(
    ENTITY_NETWORK_KEY,
    async (current) => {
      await updateEntity(entityId, { extra_props: { hide_from_chart: hidden } });
      return _applyChartVisibility(current, entityId, hidden);
    },
    {
      optimisticData: (current) => _applyChartVisibility(current, entityId, hidden),
      rollbackOnError: true,
      revalidate: true,
    },
  );
}

export const deleteEntity = async (entityId) => {
  const restOperation = del({ apiName: API_NAME, path: `entities/${entityId}` });
  const response = await restOperation.response;
  return processResponse(response);
};

export function useDeleteEntity() {
  return (entityId) => mutate(ENTITY_NETWORK_KEY, deleteEntity(entityId), { revalidate: true, populateCache: false });
}

// Replace an entity's owner-set (its incoming :OWNS cap table). The backend
// enforces Σ(pct) ≤ 1. Revalidates the network so the graph + beneficial-owner
// previews recompute.
export const setEntityOwners = async (entityId, owners) => {
  const restOperation = put({ apiName: API_NAME, path: `entities/${entityId}/owners`, options: { body: { owners } } });
  const response = await restOperation.response;
  return processResponse(response);
};

export function useSetEntityOwners() {
  return (entityId, owners) => mutate(ENTITY_NETWORK_KEY, setEntityOwners(entityId, owners), { revalidate: true, populateCache: false });
}

async function getBeneficialOwners(entityId) {
  const restOperation = get({ apiName: API_NAME, path: `entities/${entityId}/beneficial_owners` });
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetBeneficialOwners(entityId, shouldFetch = true) {
  return useSWR(
    shouldFetch && entityId ? ['BENEFICIAL_OWNERS', entityId] : null,
    () => getBeneficialOwners(entityId),
    { revalidateOnFocus: false },
  );
}

// Fiduciary roles (:HAS_PARTY) for trusts/estates — descriptive, separate from
// the :OWNS beneficial-ownership cap table.
async function getEntityParties(entityId) {
  const restOperation = get({ apiName: API_NAME, path: `entities/${entityId}/parties` });
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetEntityParties(entityId, shouldFetch = true) {
  return useSWR(
    shouldFetch && entityId ? ['ENTITY_PARTIES', entityId] : null,
    () => getEntityParties(entityId),
    { revalidateOnFocus: false },
  );
}

export const setEntityParties = async (entityId, parties) => {
  const restOperation = put({ apiName: API_NAME, path: `entities/${entityId}/parties`, options: { body: { parties } } });
  const response = await restOperation.response;
  return processResponse(response);
};

// Optimistic: the per-entity parties cache is just the saved list, so swap it in
// immediately (roles are descriptive — no beneficial-owner recompute needed),
// then persist + revalidate; roll back on failure.
export function useSetEntityParties() {
  return (entityId, parties) => mutate(
    ['ENTITY_PARTIES', entityId],
    async () => { await setEntityParties(entityId, parties); return parties; },
    { optimisticData: parties, rollbackOnError: true, revalidate: true },
  );
}

// Entity de-duplication: likely-duplicate pairs (OCR account holder vs the same
// person added in Case Details) for human-confirmed merge. Name canonicalization
// dedupes :Name nodes, never :Entity nodes — so this is the entity-level dedup.
async function getDuplicateSuggestions() {
  const restOperation = get({ apiName: API_NAME, path: 'entities/duplicate_suggestions' });
  const response = await restOperation.response;
  return processResponse(response);
}

export function useGetDuplicateSuggestions(shouldFetch = true) {
  return useSWR(
    shouldFetch ? DUP_SUGGESTIONS_KEY : null,
    () => getDuplicateSuggestions(),
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );
}

// Fold the duplicate into the keeper (keeperId survives). Moves the duplicate's
// title / ownership / role edges onto the keeper, then deletes it. Revalidate
// both the network (graph + list) and the suggestions (the merged pair is gone).
export const mergeEntities = async (keeperId, duplicateId) => {
  const restOperation = post({ apiName: API_NAME, path: `entities/${keeperId}/merge`, options: { body: { duplicate_id: duplicateId } } });
  const response = await restOperation.response;
  return processResponse(response);
};

export function useMergeEntities() {
  return async (keeperId, duplicateId) => {
    const result = await mergeEntities(keeperId, duplicateId);
    mutate(ENTITY_NETWORK_KEY);
    mutate(DUP_SUGGESTIONS_KEY);
    return result;
  };
}

// Mark a suggested pair as not-the-same so it stops being suggested.
export const dismissDuplicate = async (entityId, otherId) => {
  const restOperation = post({ apiName: API_NAME, path: `entities/${entityId}/not_duplicate`, options: { body: { other_id: otherId } } });
  const response = await restOperation.response;
  return processResponse(response);
};

export function useDismissDuplicate() {
  return async (entityId, otherId) => {
    const result = await dismissDuplicate(entityId, otherId);
    mutate(DUP_SUGGESTIONS_KEY);
    return result;
  };
}
